import { formatCount, formatMoney } from "./money";
import type { Series } from "../components/TrendChart";
import type { AffiliateTrendPoint, TrendPoint } from "./types";

/**
 * What an affiliate is actually trying to work out, expressed once.
 *
 * These three ratios are the whole of "which campaign should I do more of",
 * and they live here rather than inline in a table cell because two screens
 * show them and a disagreement between the two would be invisible.
 *
 * ---------------------------------------------------------------------------
 * Why earnings per click is the one to sort on
 * ---------------------------------------------------------------------------
 * It is the only figure that survives comparing two campaigns of different
 * sizes. Total earnings reward whichever link has been up longest; a signup
 * rate rewards traffic that signs up and never pays. Earnings per click folds
 * the whole funnel — did they arrive, did they sign up, did they buy, how much
 * — into one number per visit, which is what an affiliate is choosing between
 * when they decide where to put the next hour.
 *
 * A click here is a *visit*: `recordClick` deduplicates a visitor against a
 * link inside a window, so this is not flattered by somebody reloading.
 */
export interface LinkMetrics {
  /** Signups per click. Did the traffic care? */
  signupRate: number;
  /** Conversions per signup. Did the people who cared become customers? */
  conversionRate: number;
  /** Earnings per click, in micros. The number to compare campaigns on. */
  epcMicros: number;
}

export function linkMetrics(link: {
  clicks: number;
  referrals: number;
  conversions: number;
  earnedMicros: number;
}): LinkMetrics {
  return {
    // Guarded rather than shown as zero: a link with no clicks yet has no rate,
    // and 0% reads as a campaign that failed rather than one nobody has seen.
    signupRate: link.clicks === 0 ? Number.NaN : link.referrals / link.clicks,
    conversionRate: link.referrals === 0 ? Number.NaN : link.conversions / link.referrals,
    epcMicros: link.clicks === 0 ? Number.NaN : link.earnedMicros / link.clicks,
  };
}

/** A rate, or an em dash where there is nothing to divide by. */
export function formatRate(value: number): string {
  if (!Number.isFinite(value)) return "—";
  // One decimal below 10%, none above: a signup rate is usually a few percent
  // and "3%" throws away the difference between 2.6 and 3.4.
  return value < 0.1 ? `${(value * 100).toFixed(1)}%` : `${Math.round(value * 100)}%`;
}

export function formatEpc(micros: number): string {
  if (!Number.isFinite(micros)) return "—";
  return formatMoney(micros);
}

/*
 * The lines, named once. Colour carries meaning across both charts — accent is
 * always the top of the funnel, mint is always what converted, warn is always
 * money — so a reader who learns one chart can read the other.
 */
const COUNT = (value: number) => formatCount(value);
const MONEY = (value: number) => formatMoney(value);

/* `label` is a key under the `charts` namespace; TrendChart reads it through `t`. */
export const PROGRAMME_SERIES: Series<TrendPoint>[] = [
  { key: "signups", label: "series.signups", stroke: "var(--color-accent)", value: (p) => p.signups, format: COUNT },
  { key: "conversions", label: "series.conversions", stroke: "var(--color-mint)", value: (p) => p.conversions, format: COUNT },
  { key: "earned", label: "series.earned", stroke: "var(--color-warn)", value: (p) => p.earnedMicros, format: MONEY },
];

/**
 * Four lines for an affiliate, and clicks is the one the programme view omits.
 *
 * It is the top of their funnel and the only part they directly control. A week
 * where clicks held and signups fell is a landing-page problem; a week where
 * both fell is a traffic problem, and those need different work.
 */
export const AFFILIATE_SERIES: Series<AffiliateTrendPoint>[] = [
  { key: "clicks", label: "series.clicks", stroke: "var(--color-fg-subtle)", value: (p) => p.clicks, format: COUNT },
  { key: "signups", label: "series.signups", stroke: "var(--color-accent)", value: (p) => p.signups, format: COUNT },
  { key: "conversions", label: "series.conversions", stroke: "var(--color-mint)", value: (p) => p.conversions, format: COUNT },
  { key: "earned", label: "series.earned", stroke: "var(--color-warn)", value: (p) => p.earnedMicros, format: MONEY },
];
